import { useEffect } from "react";
import type { FieldPath } from "react-hook-form";
import { useForm, useWatch } from "react-hook-form";

import type { LanguageDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import type { FormDocument } from "@/components/DocumentInput/useDocumentInput";
import { Form } from "@/components/Form/Form";
import { FormDocumentInput } from "@/components/Form/FormDocumentInput";
import { FormInput } from "@/components/Form/FormInput";
import { Translate01Icon } from "@/components/Icons/Translate01Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { Notice } from "@/design-system/Notice";
import { getFlagIcon } from "@/helpers/flags";
import { createDocumentSizeRule, createRequiredStringRule } from "@/helpers/rules";
import { isValidHttpUrl } from "@/helpers/validation";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useSlug } from "@/hooks/useSlug";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export interface DocumentFileFormProps {
  folderName: string;
  folderId: string;
  defaultFormValues: DocumentFileFormValues;
  originalLanguageId?: LanguageDto["id"];
  isSubmitting: boolean;
  onSubmit: (values: DocumentFileFormValues) => Promise<unknown>;
  isEditMode: boolean;
}

export function DocumentFileForm(props: DocumentFileFormProps): React.ReactNode {
  const slug = useSlug();
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const formMethods = useForm<DocumentFileFormValues>({
    mode: "onChange",
    defaultValues: props.defaultFormValues,
  });

  const flagIcon = getFlagIcon({
    languageId: props.originalLanguageId ?? sessionUser.uiLanguage.id,
    width: 16,
  });
  const touchedDocumentOrLinkField =
    formMethods.formState.touchedFields.link || formMethods.formState.touchedFields.documents;

  function submit(values: DocumentFileFormValues) {
    void props.onSubmit(values);
  }

  const documents = useWatch({ control: formMethods.control, name: "documents" });
  const { trigger, setValue, getValues } = formMethods;
  useEffect(() => {
    if (documents?.[0]) {
      setValue("link", "");

      void trigger("link");
    } else {
      if (touchedDocumentOrLinkField || getValues("link")) {
        void trigger("link");
      }
    }
  }, [documents, setValue, touchedDocumentOrLinkField, trigger, getValues]);

  const title = props.isEditMode ? t("page.document.file.edit.title") : t("page.document.file.create.title");

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={title}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: props.folderName,
              to: routes.documents.documentsList({
                slug,
                folderId: props.folderId,
              }),
            },
            {
              name: title,
            },
          ]}
        />
      }
    >
      <Form.Root align="start" formMethods={formMethods} onSubmit={submit}>
        <Form.Container>
          <Form.Body>
            <Notice
              icon={<Translate01Icon />}
              variant="special"
              message={
                props.isEditMode
                  ? t("common.label.you-are-editing-in-original-content-language")
                  : t("common.label.your-input-will-be-auto-translated")
              }
            />
            <FormField
              required
              label={t("page.document.file.create.name")}
              description={t("page.document.file.create.name.description")}
              actions={flagIcon}
            >
              <FormInput<DocumentFileFormValues>
                data-testid="documents-file-name"
                name="name"
                placeholder={t("page.document.file.create.name.placeholder")}
                rules={{
                  validate: {
                    required: createRequiredStringRule(t, "page.document.file.create.name"),
                  },
                  maxLength: {
                    value: NAME_MAX_LENGTH,
                    message: t("components.form.error.max-length", {
                      length: NAME_MAX_LENGTH,
                    }),
                  },
                }}
              />
            </FormField>

            <FormField required label={t("page.document.file.create.file")}>
              <FormDocumentInput<DocumentFileFormValues, "documents">
                data-testid="documents-file-input"
                name="documents"
                rules={{
                  validate: {
                    required: () => {
                      const values = formMethods.getValues();
                      if (!values.link && !values.documents?.[0]) {
                        return t("components.form.error.either-required", {
                          inputName1: t("page.document.file.create.file"),
                          inputName2: t("page.document.file.create.link"),
                        });
                      }
                    },
                    size: createDocumentSizeRule({
                      t,
                    }),
                  },
                }}
              />
            </FormField>

            <span className="block text-body-emphasis text-text-secondary uppercase">
              {t("page.document.file.create.or")}
            </span>

            <FormField label={t("page.document.file.create.link")} required>
              <FormInput<DocumentFileFormValues, "link">
                data-testid="documents-file-link"
                name="link"
                placeholder={t("page.document.file.create.link.placeholder")}
                disabled={documents && documents.length > 0}
                rules={{
                  validate: {
                    required: () => {
                      const values = formMethods.getValues();
                      if (!values.link && !values.documents?.[0]) {
                        return t("components.form.error.either-required", {
                          inputName1: t("page.document.file.create.file"),
                          inputName2: t("page.document.file.create.link"),
                        });
                      }
                    },
                    isValid: (value) => {
                      if (value && !isValidHttpUrl(value.trim())) {
                        return t("components.form.error.invalid-url");
                      }
                    },
                  },
                  deps: ["documents"] satisfies FieldPath<DocumentFileFormValues>[],
                }}
              />
            </FormField>
          </Form.Body>
          <Form.Footer
            renderPrimaryAction={() => (
              <Button data-testid="documents-file-submit" type="submit" isLoading={props.isSubmitting}>
                {props.isEditMode ? t("common.action.save") : t("common.action.create")}
              </Button>
            )}
          />
        </Form.Container>
      </Form.Root>
    </DocumentPage>
  );
}

export interface DocumentFileFormValues {
  name: string;
  documents: FormDocument[];
  link: string;
}

const NAME_MAX_LENGTH = 40;
